import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons } from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { getTaskLabel } from '../../task-labels';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';

export const ApproveCompanyDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest } = context;

  const router = useRouter();

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  const taskWithWorkRequest = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) => srt.legacyServiceRequestTaskTaskId === 'FILL_CORPORATION_DETAILS'
    );

  // Sibling fill task is the fixer; issues reported from this review task clear on its completion.
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    taskWithWorkRequest,
    task
  );

  const navigateToVerifyWorkRequest = () => {
    router.push(
      `/work-request/${taskWithWorkRequest?.workRequestId}/verify/ind`
    );
  };

  const getLabel = (role: TaskParty, status: TaskStatus) =>
    getTaskLabel(
      getServiceRequestServiceId(serviceRequest),
      task.legacyServiceRequestTaskTaskId,
      role,
      status,
      task.title
    ) ?? task.title;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) =>
      ServiceRequestTaskIconConfig[hasOpenReportedIssue ? TaskStatus.IN_PROGRESS : status],
    getStateConfig: (state) => {
      // Restored legacy ISSUE_REPORTED arms — the workflow reject encodes as COMPLETED + open issue.
      if (hasOpenReportedIssue) {
        const issueMap = {
          [TaskParty.CLIENT]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                timestamp={task.updatedAt}
                status={TaskStatus.IN_PROGRESS}
              />
            ),
          },
          [TaskParty.AGENT]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={navigateToVerifyWorkRequest}
              >
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                timestamp={task.updatedAt}
                status={TaskStatus.IN_PROGRESS}
              />
            ),
          },
        };
        const issueConfig = issueMap[state.role];
        if (issueConfig) return issueConfig;
      }

      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.COMPLETED)}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.BACKLOG)}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={navigateToVerifyWorkRequest}
                variant="primary"
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.BACKLOG)}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={navigateToVerifyWorkRequest}
                >
                  View <Icons.ArrowNarrowRight className="w-4" />
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.COMPLETED)}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{
        role,
        status: task.status,
      }}
      context={context}
      config={config}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
